import { logger } from '../util/log.util.js'
import { sentry } from '../util/sentry.util.js'

type StorageDriverType = 'localStorage' | 'sessionStorage'
interface StorageServiceConf {
  prefix?: string
}
interface StorageService {
  setConf: (conf: StorageServiceConf) => void
  has: (k: string) => boolean
  get: (k: string) => string | undefined
  put: (k: string, v: string) => void
  remove: (k: string) => void
  clear: () => void
}

class StorageDriver implements StorageService {
  constructor(private readonly type: StorageDriverType) {
    let g: any

    try {
      g = window
    } catch {}

    try {
      g = global
    } catch {}

    try {
      if ('localStorage' in g && g.localStorage !== undefined) {
        this.driver = this.type === 'localStorage' ? g.localStorage : g.sessionStorage
        this.driver.setItem('testKey', '1')
        this.driver.removeItem('testKey')
      }
    } catch (err) {
      sentry.captureException(err)
    }

    if (!this.driver) logger.warn(`Web Storage API: (${this.type}) is not supported!`)
  }

  private readonly driver!: Storage

  private readonly conf: StorageServiceConf = {
    prefix: '',
  }

  setConf(_conf: StorageServiceConf): void {
    Object.assign(this.conf, _conf)
  }

  has(k: string): boolean {
    return !!this.get(k)
  }

  get(k: string): string | undefined {
    if (!this.driver) return undefined
    return this.driver.getItem(this.conf.prefix + k) || undefined
  }

  getJson<T>(k: string): T | undefined {
    const v = this.get(k)
    if (!v) return undefined
    return JSON.parse(v)
  }

  put(k: string, v: string): string | undefined {
    if (!this.driver) return undefined

    try {
      this.driver.setItem(this.conf.prefix + k, v)
      return v
    } catch (err) {
      logger.error(`Web Storage API: (${this.type}) error (k=${k}, v=${v})`, err)
    }
  }

  putJson(k: string, v: any): void {
    this.put(k, JSON.stringify(v))
  }

  remove(k: string): string | undefined {
    if (!this.driver) return undefined

    this.driver.removeItem(this.conf.prefix + k)
    return k
  }

  clear(): boolean {
    if (!this.driver) return false

    this.driver.clear()
    return true
  }

  keys(): string[] {
    if (!this.driver) return []

    try {
      return Object.keys(this.driver)
    } catch (err) {
      sentry.captureException(err)
      return []
    }
  }
}

export const storageService = new StorageDriver('localStorage')
export const sessionStorageService = new StorageDriver('sessionStorage')
